// Theme utility functions for persistence and loading
// This module handles theme persistence both locally (localStorage) and remotely (user profile)
// When a user is logged in, theme preferences are saved to their profile via the backend API
// This ensures theme settings persist across devices and browser sessions
import axiosInstance from "./axiosInstance";

export interface ThemeData {
  primary: string;
  primaryLight: string;
  primaryDark: string;
  isCustom: boolean;
  selectedScheme: string;
}

// Get current user from localStorage
const getCurrentUser = () => {
  try {
    return JSON.parse(localStorage.getItem("user") || "{}");
  } catch {
    return {};
  }
};

// Get current theme from CSS variables
export const getCurrentTheme = (): ThemeData => {
  const root = document.documentElement;
  const primary =
    getComputedStyle(root).getPropertyValue("--primary-color").trim() ||
    "#1D2B56";
  const primaryLight =
    getComputedStyle(root).getPropertyValue("--primary-light").trim() ||
    "#2a3a6a";
  const primaryDark =
    getComputedStyle(root).getPropertyValue("--primary-dark").trim() ||
    "#162142";

  return {
    primary,
    primaryLight,
    primaryDark,
    isCustom: false,
    selectedScheme: "default",
  };
};

// Save theme to user profile via API
// This function updates the user's profile with their theme preferences
// The theme data is stored in the user's attributes object
const saveThemeToUserProfile = async (themeData: ThemeData): Promise<void> => {
  try {
    const user = getCurrentUser();
    const isAuthenticated = localStorage.getItem("isAuthenticated") === "true";

    if (!user.user?.id || !isAuthenticated) {
      console.warn(
        "User not authenticated or no user ID, theme will only be saved locally"
      );
      return;
    }

    // Get current user data from API
    const response = await axiosInstance.get(
      `/gpstracker/userhandler/${user.user?.id}`
    );
    
    if (!response.data?.responseData) {
      console.warn(
        "Failed to fetch user data, theme will only be saved locally"
      );
      return;
    }

    const userData = response.data.responseData;

    // Update user attributes with theme data
    const updatedUser = {
      ...userData,
      attributes: {
        ...userData.attributes,
        theme: themeData,
      },
    };

    // Remove can_create_device field if it exists (similar to PointsTransfer.tsx)
    delete updatedUser.can_create_device;

    // Save updated user data to API
    await axiosInstance.put(`/gpstracker/userhandler/${user.user?.id}`, updatedUser);
    console.log("Theme saved to user profile successfully");
  } catch (error) {
    console.error("Error saving theme to user profile:", error);
    // Don't throw error, just log it - theme will still be saved locally
  }
};

// Load theme from user profile
const loadThemeFromUserProfile = async (): Promise<ThemeData | null> => {
  try {
    const user = getCurrentUser();
    const isAuthenticated = localStorage.getItem("isAuthenticated") === "true";

    if (!user.user?.id || !isAuthenticated) {
      console.log(
        "User not authenticated or no user ID, skipping theme load from profile"
      );
      return null;
    }

    console.log('Loading theme from user profile for user:', user.id);

    // Fetch user data from API
    const response = await axiosInstance.get(
      `/gpstracker/userhandler/${user.user?.id}`
    );
    
    if (!response.data?.responseData) {
      console.log('No user data found in response');
      return null;
    }

    const userData = response.data.responseData;
    const themeData = userData.attributes?.theme;

    console.log('User data loaded:', { userData, themeData });

    if (themeData && typeof themeData === "object") {
      console.log("Theme loaded from user profile:", themeData);
      return themeData as ThemeData;
    }

    console.log('No theme data found in user attributes');
    return null;
  } catch (error) {
    console.error("Error loading theme from user profile:", error);
    return null;
  }
};

export const loadSavedTheme = async (): Promise<void> => {
  try {
    const isAuthenticated = localStorage.getItem("isAuthenticated") === "true";
    const user = getCurrentUser();

    console.log("Loading saved theme...", {
      isAuthenticated,
      userId: user?.user?.id,
    });

    // If user is authenticated, try to load from user profile first
    if (isAuthenticated && user?.user?.id) {
      const userTheme = await loadThemeFromUserProfile();

      if (userTheme) {
        console.log("Applying theme from user profile:", userTheme);
        // Apply theme from user profile
        const root = document.documentElement;
        root.style.setProperty("--primary-color", userTheme.primary);
        root.style.setProperty("--primary-light", userTheme.primaryLight);
        root.style.setProperty("--primary-dark", userTheme.primaryDark);

        // Also save to localStorage for offline access
        localStorage.setItem("acumen-theme", JSON.stringify(userTheme));

        // Force a re-render by dispatching a custom event
        window.dispatchEvent(
          new CustomEvent("themeChanged", { detail: userTheme })
        );
        return;
      } else {
        console.log("No theme found in user profile, checking localStorage");
      }
    }

    // Fallback to localStorage (for non-authenticated users or when user profile has no theme)
    const savedTheme = localStorage.getItem("acumen-theme");
    if (savedTheme) {
      console.log("Applying theme from localStorage");
      const themeData: ThemeData = JSON.parse(savedTheme);
      const root = document.documentElement;
      root.style.setProperty("--primary-color", themeData.primary);
      root.style.setProperty("--primary-light", themeData.primaryLight);
      root.style.setProperty("--primary-dark", themeData.primaryDark);

      // Force a re-render by dispatching a custom event
      window.dispatchEvent(
        new CustomEvent("themeChanged", { detail: themeData })
      );
    } else {
      console.log("No saved theme found, using default");
      // Apply default theme
      const root = document.documentElement;
      root.style.setProperty("--primary-color", "#1D2B56");
      root.style.setProperty("--primary-light", "#2a3a6a");
      root.style.setProperty("--primary-dark", "#162142");
    }
  } catch (error) {
    console.error("Error loading saved theme:", error);
    // Fallback to default theme
    const root = document.documentElement;
    root.style.setProperty("--primary-color", "#1D2B56");
    root.style.setProperty("--primary-light", "#2a3a6a");
    root.style.setProperty("--primary-dark", "#162142");
  }
};

export const saveTheme = async (themeData: ThemeData): Promise<void> => {
  try {
    // Save to localStorage for immediate access
    localStorage.setItem("acumen-theme", JSON.stringify(themeData));

    // Apply the theme immediately
    const root = document.documentElement;
    root.style.setProperty("--primary-color", themeData.primary);
    root.style.setProperty("--primary-light", themeData.primaryLight);
    root.style.setProperty("--primary-dark", themeData.primaryDark);

    // Save to user profile (async, don't wait for it)
    saveThemeToUserProfile(themeData);

    // Force a re-render by dispatching a custom event
    window.dispatchEvent(
      new CustomEvent("themeChanged", { detail: themeData })
    );
  } catch (error) {
    console.error("Error saving theme:", error);
  }
};

export const resetTheme = async (): Promise<void> => {
  try {
    const defaultTheme = {
      primary: "#1D2B56",
      primaryLight: "#2a3a6a",
      primaryDark: "#162142",
      isCustom: false,
      selectedScheme: "default",
    };

    // Remove from localStorage
    localStorage.removeItem("acumen-theme");

    // Apply default theme
    const root = document.documentElement;
    root.style.setProperty("--primary-color", defaultTheme.primary);
    root.style.setProperty("--primary-light", defaultTheme.primaryLight);
    root.style.setProperty("--primary-dark", defaultTheme.primaryDark);

    // Save default theme to user profile
    await saveThemeToUserProfile(defaultTheme);

    // Force a re-render by dispatching a custom event
    window.dispatchEvent(
      new CustomEvent("themeChanged", { detail: defaultTheme })
    );
  } catch (error) {
    console.error("Error resetting theme:", error);
  }
};

// Function to load theme immediately after user login
export const loadThemeOnLogin = async (): Promise<void> => {
  try {
    const user = getCurrentUser();
    if (user?.user?.id) {
      console.log("Loading theme on login for user:", user.id);
      await loadSavedTheme();
    }
  } catch (error) {
    console.error("Error loading theme on login:", error);
  }
};
